import { useState } from "react";
import { motion } from "framer-motion";
import { SectionHeading } from "./NewsSection";
import TrainingCasesSection, { Case } from "./TrainingCasesSection";
import CasebookDialog from "@/components/CasebookDialog";
import implementationData from "@/data/implementation-cases.json";
import trainingData from "@/data/training-cases.json";

const trainingCases = (trainingData as Case[]).map((c) => ({
  ...c,
  fieldLabels: {
    audience: "Аудитория",
    request: "Запрос",
    result: "Результат",
  },
}));
const implementationCases = (implementationData as Case[]).map((c) => ({
  ...c,
  tagLabel: "Разработка",
  filterTags: ["Разработка"],
}));

const stableShuffle = (items: Case[]) => {
  const shuffled = [...items];
  let seed = 0x2f6e2b1;

  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    seed = (seed * 1664525 + 1013904223) >>> 0;
    const target = seed % (index + 1);
    [shuffled[index], shuffled[target]] = [shuffled[target], shuffled[index]];
  }

  return shuffled;
};

const combinedCases: Case[] = [...trainingCases, ...implementationCases];
const transformationCases = combinedCases.filter((item) => item.format === "AI-трансформация");
const otherCases = stableShuffle(
  combinedCases.filter((item) => item.format !== "AI-трансформация"),
);
const allCases: Case[] = [...transformationCases, ...otherCases];

type PartnersSectionProps = {
  layout?: "carousel" | "grid";
  headingAs?: "h1" | "h2";
  headingVariant?: "section" | "page";
};

const PartnersSection = ({ layout = "carousel", headingAs = "h2", headingVariant = "section" }: PartnersSectionProps) => {
  const [casebookOpen, setCasebookOpen] = useState(false);
  const Heading = headingAs;

  return (
    <section id="partners" className="py-16 md:py-24 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
          className={headingVariant === "page" ? "" : "text-center"}
        >
          {headingVariant === "page" ? (
            <Heading className="font-heading text-4xl font-medium leading-[1.08] tracking-normal sm:text-5xl lg:text-[56px]">
              Нам доверяют <span className="text-accent">лидеры рынка</span>
            </Heading>
          ) : (
            <SectionHeading as={headingAs}>
              <span className="block sm:inline">Нам доверяют</span>{" "}
              <span className="block sm:inline text-accent">лидеры рынка</span>
            </SectionHeading>
          )}
          {headingVariant === "page" && (
            <button
              type="button"
              onClick={() => setCasebookOpen(true)}
              className="mt-8 wmt-cta wmt-cta--dark"
            >
              <span className="optical-center">Получить полный кейсбук</span>
            </button>
          )}
        </motion.div>
      </div>

      <TrainingCasesSection
        items={allCases}
        fieldLabels={{
          audience: "Задача",
          request: "Решение",
          result: "Результаты",
        }}
        ctaLabel="Обсудить похожий проект"
        ctaHref={layout === "grid" ? "/#contact" : "#contact"}
        showFormatTags={true}
        showFilters={layout === "grid"}
        layout={layout}
      />

      {headingVariant !== "page" && (
        <div className="relative z-30 mx-auto -mt-8 flex max-w-7xl justify-center px-4 sm:px-6 md:-mt-12 lg:px-8">
          <button
            type="button"
            onClick={() => setCasebookOpen(true)}
            className="wmt-cta wmt-cta--dark group pointer-events-auto relative z-30"
          >
            <span className="pointer-events-none optical-center">Получить полный кейсбук</span>
          </button>
        </div>
      )}

      <CasebookDialog open={casebookOpen} onOpenChange={setCasebookOpen} />
    </section>
  );
};

export default PartnersSection;
